import { ArrowLeft, CheckCircle2, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { currency, numberOne, type QuoteCalculation } from "../calculations";
import { usePricingStore } from "../store";

export function PrintQuote({ calculation }: { calculation: QuoteCalculation }) {
  const navigate = useNavigate();
  const { activeProject, state } = usePricingStore();
  const isDiscovery = calculation.status === "仅需求梳理";
  const total = isDiscovery ? calculation.discoveryTotal : calculation.customerTotal;
  const validUntil = new Date(activeProject.quoteDate || Date.now());
  validUntil.setDate(validUntil.getDate() + state.settings.validityDays);

  if (!calculation.canPrint) {
    return (
      <main className="quote-blocked">
        <div className="quote-blocked-card">
          <span className="status-dot danger" />
          <p className="eyebrow">客户报价尚未就绪</p>
          <h1>{calculation.status}</h1>
          <p>{calculation.statusMessage}</p>
          <button className="button primary" type="button" onClick={() => navigate("/wbs")}><ArrowLeft size={17} />返回调整</button>
        </div>
      </main>
    );
  }

  const serviceRows = isDiscovery
    ? [{ platform: "付费需求梳理", actualDays: activeProject.discoveryDays, laborFee: calculation.discoveryUntaxed }]
    : calculation.platformSummaries;

  return (
    <main className="quote-page">
      <div className="quote-controls">
        <button className="button secondary" type="button" onClick={() => navigate(-1)}><ArrowLeft size={17} />返回计算器</button>
        <span>打印视图仅显示客户可见内容</span>
        <button className="button primary" type="button" onClick={() => window.print()}><Printer size={17} />打印 / 保存 PDF</button>
      </div>

      <article className="quote-document">
        <header className="quote-header">
          <div>
            <div className="quote-logo">OC</div>
            <p>杭州高级全栈开发服务</p>
          </div>
          <div className="quote-title">
            <p>PROPOSAL</p>
            <h1>{isDiscovery ? "需求梳理报价单" : "技术开发服务报价单"}</h1>
            <span>报价编号：{activeProject.quoteNumber}</span>
          </div>
        </header>

        <section className="quote-hero">
          <div>
            <p className="eyebrow">项目名称</p>
            <h2>{activeProject.projectName || "未命名项目"}</h2>
            <p>{activeProject.description || "按双方确认的需求范围提供技术开发与交付服务。"}</p>
          </div>
          <div className="quote-total">
            <span>客户总价</span>
            <strong>{currency(total)}</strong>
            <small>{activeProject.taxRate > 0 ? `含开票附加 ${(activeProject.taxRate * 100).toFixed(0)}%` : "未税报价"}</small>
          </div>
        </section>

        <section className="quote-meta">
          <div><span>客户</span><strong>{activeProject.clientName || "待填写"}</strong></div>
          <div><span>联系人</span><strong>{activeProject.contact || "待填写"}</strong></div>
          <div><span>报价日期</span><strong>{activeProject.quoteDate}</strong></div>
          <div><span>有效期至</span><strong>{validUntil.toISOString().slice(0, 10)}</strong></div>
        </section>

        <section className="quote-section">
          <div className="quote-section-title"><span>01</span><h3>{isDiscovery ? "需求梳理服务" : "平台与服务明细"}</h3></div>
          <table className="quote-table">
            <thead><tr><th>服务范围</th><th>预计人日</th><th>服务费</th></tr></thead>
            <tbody>
              {serviceRows.map((row) => <tr key={row.platform}><td>{row.platform}</td><td>{numberOne(row.actualDays)}</td><td>{currency(row.laborFee)}</td></tr>)}
            </tbody>
            {!isDiscovery ? <tfoot><tr><td>开发服务小计（含准备金与折扣）</td><td>{numberOne(calculation.actualDays)}</td><td>{currency(calculation.discountedUntaxed - calculation.addonCustomerAmount)}</td></tr></tfoot> : null}
          </table>
          {!isDiscovery && calculation.addonCustomerAmount > 0 ? (
            <table className="quote-table quote-addons">
              <thead><tr><th>附加费用</th><th>供应方式</th><th>客户报价</th></tr></thead>
              <tbody>{activeProject.addonItems.filter((item) => item.included && item.customerAmount > 0).map((item) => <tr key={item.id}><td>{item.category} · {item.item}</td><td>{item.supplyMode}</td><td>{currency(item.customerAmount)}</td></tr>)}</tbody>
            </table>
          ) : null}
        </section>

        <section className="quote-section">
          <div className="quote-section-title"><span>02</span><h3>付款节点</h3></div>
          <div className="milestone-grid">
            {calculation.paymentMilestones.map((milestone, index) => (
              <article key={milestone.name}><b>{String(index + 1).padStart(2, "0")}</b><span>{milestone.name} · {(milestone.rate * 100).toFixed(0)}%</span><strong>{currency(milestone.amount)}</strong><small>{milestone.condition}</small></article>
            ))}
          </div>
        </section>

        {!isDiscovery && activeProject.maintenanceOption !== "无" ? (
          <section className="quote-section maintenance-quote">
            <div className="quote-section-title"><span>03</span><h3>可选年度维护</h3></div>
            <div><CheckCircle2 size={18} /><p><strong>{activeProject.maintenanceOption}</strong><br />独立可选，不计入本页开发总价</p><b>{currency(activeProject.maintenanceOption === "基础维护" ? calculation.maintenanceBasic : calculation.maintenanceStandard)} / 年</b></div>
          </section>
        ) : null}

        <section className="quote-terms">
          <h3>交付与商务说明</h3>
          <ol>
            <li>报价包含需求澄清、技术方案、开发、自测、部署与技术文档；未列明的第三方采购另计。</li>
            <li>需求确认后的新增功能按 ¥{state.settings.dayRate.toLocaleString("zh-CN")}/人日另行估算，最低 0.5 人日。</li>
            <li>交付后提供 {state.settings.warrantyDays} 天约定范围内缺陷质保；新增功能不属于质保或维护范围。</li>
            <li>源码和正式部署权限在合同约定款项结清后交付；单个未付款阶段不超过 20 个工作日。</li>
          </ol>
        </section>

        <footer className="quote-footer">
          <span>报价有效期 {state.settings.validityDays} 天</span>
          <span>{activeProject.notes || "最终范围与排期以双方确认的合同及需求清单为准。"}</span>
          <b>{currency(total)}</b>
        </footer>
      </article>
    </main>
  );
}
